import { FLAG_DEFAULTS, type FlagSnapshot, type FlagSnapshotSource } from './flags.generated'

/** Shared empty `evaluated_at` so missing bodies do not mint a fresh timestamp. */
export const EMPTY_FLAG_EVALUATED_AT = new Date(0).toISOString()

function asSource(value: unknown): FlagSnapshotSource {
  if (value === 'postgres' || value === 'override' || value === 'defaults') return value
  return 'defaults'
}

/**
 * Parse GET /api/v1/flags/snapshot JSON. Unknown keys are dropped; missing
 * keys fill from {@link FLAG_DEFAULTS}. Missing `evaluated_at` falls back to
 * the Unix epoch ({@link EMPTY_FLAG_EVALUATED_AT}) so callers share one empty
 * snapshot instead of minting a fresh timestamp.
 */
export function parseFlagSnapshot(raw: unknown): FlagSnapshot {
  const body = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const flags: Record<string, boolean> = { ...FLAG_DEFAULTS }
  if (body.flags && typeof body.flags === 'object') {
    for (const [key, value] of Object.entries(body.flags as Record<string, unknown>)) {
      if (typeof value === 'boolean' && key in FLAG_DEFAULTS) {
        flags[key] = value
      }
    }
  }
  return {
    flags: flags as FlagSnapshot['flags'],
    evaluated_at:
      typeof body.evaluated_at === 'string' && body.evaluated_at.trim()
        ? body.evaluated_at
        : EMPTY_FLAG_EVALUATED_AT,
    source: asSource(body.source)
  }
}

/**
 * Parse a pin map (Redis / queue envelope). Only boolean entries are kept.
 * Empty or non-object input returns undefined so callers can omit the field.
 */
export function parseFlagMap(raw: unknown): Record<string, boolean> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const out: Record<string, boolean> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'boolean') out[key] = value
  }
  return Object.keys(out).length ? out : undefined
}
